import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import HomeSection from '../HomeSection';
import WeekAvailabilityGrid from '../../../molecules/WeekAvailabilityGrid';
import EditorialNotesCard from '../../../molecules/EditorialNotesCard';
import EditableText from '../../../molecules/EditableText';
import {
  serializeCommitmentDetails,
  COMMITMENT_DAY_KEYS,
} from '../../../../utils/commitment';
import './index.scss';

/**
 * Reverse-mapping from the grid's 3-letter day IDs back to the canonical
 * commitment day keys ("monday", "tuesday", …) used by the backend.
 */
const SHORT_TO_FULL_DAY = {
  mon: 'monday',
  tue: 'tuesday',
  wed: 'wednesday',
  thu: 'thursday',
  fri: 'friday',
  sat: 'saturday',
  sun: 'sunday',
};

const FULL_TO_SHORT_DAY = Object.entries(SHORT_TO_FULL_DAY)
  .reduce((acc, [shortDay, fullDay]) => ({ ...acc, [fullDay]: shortDay }), {});

const normalizeSchedule = (schedule = []) => COMMITMENT_DAY_KEYS.map((dayKey) => {
  const existing = schedule.find((entry) => entry?.day === dayKey);
  const slots = Array.isArray(existing?.slots)
    ? existing.slots.map((slot) => String(slot)).filter(Boolean)
    : [];
  return { day: dayKey, slots: Array.from(new Set(slots)) };
});

const createDraftDetails = (commitmentDetails) => ({
  schedule: normalizeSchedule(commitmentDetails?.schedule || []),
  frequency: commitmentDetails?.frequency || '',
  notes: commitmentDetails?.notes || '',
});

const toGridSchedule = (schedule = []) => schedule.map((entry) => ({
  day: FULL_TO_SHORT_DAY[entry.day] || entry.day,
  slots: Array.isArray(entry.slots) ? entry.slots : [],
}));

export default function HomeCommitmentSection({
  id = 'commitment',
  eyebrow,
  title,
  side,
  weekAriaLabel,
  notesLabel,
  notesValue,
  notesHint,
  editing = false,
  commitmentDetails,
  onFieldSave,
}) {
  const incomingDraft = useMemo(
    () => createDraftDetails(commitmentDetails),
    [commitmentDetails]
  );
  const draftDetailsRef = useRef(incomingDraft);
  const saveQueueRef = useRef(Promise.resolve());
  const [draftSchedule, setDraftSchedule] = useState(incomingDraft.schedule);
  const [pendingSaveCount, setPendingSaveCount] = useState(0);

  useEffect(() => {
    if (pendingSaveCount > 0) return;
    draftDetailsRef.current = incomingDraft;
    setDraftSchedule(incomingDraft.schedule);
  }, [incomingDraft, pendingSaveCount]);

  const queueCommitmentSave = useCallback((nextDraft) => {
    if (!onFieldSave) {
      return Promise.resolve();
    }

    const serialized = serializeCommitmentDetails(nextDraft);
    setPendingSaveCount((count) => count + 1);

    const queuedSave = saveQueueRef.current
      .catch(() => null)
      .then(() => onFieldSave('commitment', serialized));

    saveQueueRef.current = queuedSave.catch(() => null);

    queuedSave
      .catch((err) => {
        console.error('Failed to save commitment details', err);
      })
      .finally(() => {
        setPendingSaveCount((count) => Math.max(0, count - 1));
      });

    return queuedSave;
  }, [onFieldSave]);

  const handleNotesSave = useCallback(async (nextNotes) => {
    const merged = {
      ...draftDetailsRef.current,
      notes: nextNotes || '',
    };
    draftDetailsRef.current = merged;
    await queueCommitmentSave(merged);
  }, [queueCommitmentSave]);

  const handleSlotToggle = useCallback((shortDayId, slotId) => {
    const fullDayId = SHORT_TO_FULL_DAY[shortDayId] || shortDayId;
    const nextSchedule = draftDetailsRef.current.schedule.map((entry) => {
      if (entry.day !== fullDayId) return entry;
      const slotsSet = new Set(entry.slots);
      if (slotsSet.has(slotId)) {
        slotsSet.delete(slotId);
      } else {
        slotsSet.add(slotId);
      }
      return { day: entry.day, slots: Array.from(slotsSet) };
    });
    const merged = {
      ...draftDetailsRef.current,
      schedule: nextSchedule,
    };
    draftDetailsRef.current = merged;
    setDraftSchedule(nextSchedule);
    queueCommitmentSave(merged).catch(() => null);
  }, [queueCommitmentSave]);

  const editingNotesValue = commitmentDetails?.notes || '';
  const gridSchedule = useMemo(() => toGridSchedule(draftSchedule), [draftSchedule]);

  return (
    <HomeSection
      id={id}
      eyebrow={eyebrow}
      title={title}
      side={side}
      showSide={editing}
      className="home-commitment"
    >
      <div className="home-commitment__grid">
        <WeekAvailabilityGrid
          schedule={gridSchedule}
          ariaLabel={weekAriaLabel}
          editing={editing}
          onSlotToggle={handleSlotToggle}
        />
        {editing ? (
          <aside className="ed-notes-card ed-notes-card--editing">
            <div className="ed-notes-card__label">{notesLabel}</div>
            <div className="ed-notes-card__value">
              <EditableText
                editing
                multiline
                rows={4}
                value={editingNotesValue}
                placeholder="Out of town summers, off-grid on weekends, etc."
                ariaLabel="Commitment notes"
                maxLength={1200}
                onSave={handleNotesSave}
                inputClassName="editable-text__textarea--seamless"
              />
            </div>
            {notesHint ? (
              <>
                <div className="ed-notes-card__divider" />
                <div className="ed-notes-card__hint">{notesHint}</div>
              </>
            ) : null}
          </aside>
        ) : (
          <EditorialNotesCard
            label={notesLabel}
            value={notesValue}
            hint={null}
          />
        )}
      </div>
    </HomeSection>
  );
}

HomeCommitmentSection.propTypes = {
  id: PropTypes.string,
  eyebrow: PropTypes.node.isRequired,
  title: PropTypes.node.isRequired,
  side: PropTypes.node,
  schedule: PropTypes.array,
  weekAriaLabel: PropTypes.string,
  notesLabel: PropTypes.node,
  notesValue: PropTypes.node,
  notesHint: PropTypes.node,
  editing: PropTypes.bool,
  commitmentDetails: PropTypes.shape({
    schedule: PropTypes.array,
    frequency: PropTypes.string,
    notes: PropTypes.string,
  }),
  onFieldSave: PropTypes.func,
};
